import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { MapPin, Star, AlertCircle, ArrowRight, Building } from "lucide-react";

const NeighborhoodsPage = () => {
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNeighborhoods();
  }, []);

  const fetchNeighborhoods = async () => {
    try {
      const response = await axios.get(`${API}/neighborhoods`);
      setNeighborhoods(response.data);
    } catch (error) {
      console.error("Failed to fetch neighborhoods:", error);
    }
    setLoading(false);
  };

  // Default neighborhoods if none in DB
  const defaultNeighborhoods = [
    {
      id: "1",
      name: "Houston Downtown",
      vibe: "Urban energy with high-rise living and walkable entertainment",
      avg_rent: "$1,800 - $3,500",
      highlights: ["Close to work", "Nightlife", "Public transit"],
      hidden_catches: ["Parking is expensive", "Can be noisy on weekends"],
      description: "The heart of Houston with stunning skyline views and endless entertainment options."
    },
    {
      id: "2",
      name: "Katy",
      vibe: "Family-friendly suburban living with excellent schools",
      avg_rent: "$1,400 - $2,200",
      highlights: ["Top-rated schools", "Safe neighborhoods", "Shopping centers"],
      hidden_catches: ["Long commute to downtown", "Car is essential"],
      description: "A growing suburb perfect for families looking for space and community."
    },
    {
      id: "3",
      name: "Sugar Land",
      vibe: "Upscale suburban feel with diverse dining and culture",
      avg_rent: "$1,500 - $2,500",
      highlights: ["Diverse community", "Great restaurants", "Town center lifestyle"],
      hidden_catches: ["Higher property taxes", "Traffic on weekends"],
      description: "Master-planned community with a perfect blend of suburban comfort and urban amenities."
    },
    {
      id: "4",
      name: "The Woodlands",
      vibe: "Nature-integrated living with resort-style amenities",
      avg_rent: "$1,600 - $3,000",
      highlights: ["Beautiful trails", "Lake activities", "Upscale shopping"],
      hidden_catches: ["Far from downtown", "Higher cost of living"],
      description: "A lush, master-planned community that feels like a permanent vacation."
    },
    {
      id: "5",
      name: "Conroe",
      vibe: "Small-town charm with growing opportunities",
      avg_rent: "$1,200 - $1,800",
      highlights: ["Affordable", "Lake Conroe access", "Growing job market"],
      hidden_catches: ["Limited public transit", "Fewer dining options"],
      description: "An affordable option north of Houston with lakeside living possibilities."
    }
  ];

  const displayNeighborhoods = neighborhoods.length > 0 ? neighborhoods : defaultNeighborhoods;

  return (
    <div className="pt-20 min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-[#0A2342] py-16 md:py-24">
        <div className="container-main text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-flex items-center px-4 py-2 bg-[#DE6E4B] rounded-full text-white text-sm font-medium mb-6">
              <MapPin className="w-4 h-4 mr-2" />
              Houston Area Guides
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 font-['Playfair_Display']">
              Neighborhood Guides
            </h1>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">
              The real scoop on Houston's best neighborhoods. Not just listings, but the actual vibes, prices, and insider tips.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Neighborhoods Grid */}
      <section className="py-16">
        <div className="container-main">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2342]"></div>
            </div>
          ) : (
            <div className="space-y-12">
              {displayNeighborhoods.map((neighborhood, index) => (
                <motion.div
                  key={neighborhood.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl transition-shadow"
                  data-testid={`neighborhood-${neighborhood.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="grid md:grid-cols-3 gap-0">
                    {/* Image/Header Section */}
                    <div className="bg-[#0A2342] p-8 flex flex-col justify-center">
                      <div className="flex items-center mb-4">
                        <Building className="w-8 h-8 text-[#DE6E4B] mr-3" />
                        <h2 className="text-2xl font-bold text-white font-['Playfair_Display']">
                          {neighborhood.name}
                        </h2>
                      </div>
                      <p className="text-slate-300 text-sm mb-4">{neighborhood.description}</p>
                      <div className="bg-[#DE6E4B] rounded-lg px-4 py-2 inline-block w-fit">
                        <span className="text-white font-medium">{neighborhood.avg_rent}/mo</span>
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="md:col-span-2 p-8">
                      {/* Vibe */}
                      <div className="mb-6">
                        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">
                          The Vibe
                        </h3>
                        <p className="text-[#0A2342] text-lg">{neighborhood.vibe}</p>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Highlights */}
                        <div>
                          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center">
                            <Star className="w-4 h-4 text-[#DE6E4B] mr-2" />
                            Highlights
                          </h3>
                          <ul className="space-y-2">
                            {neighborhood.highlights.map((highlight) => (
                              <li key={highlight} className="flex items-center text-slate-600">
                                <span className="w-2 h-2 bg-green-500 rounded-full mr-2" />
                                {highlight}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Hidden Catches */}
                        <div>
                          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center">
                            <AlertCircle className="w-4 h-4 text-amber-500 mr-2" />
                            Things to Know
                          </h3>
                          <ul className="space-y-2">
                            {neighborhood.hidden_catches.map((catch_) => (
                              <li key={catch_} className="flex items-center text-slate-600">
                                <span className="w-2 h-2 bg-amber-500 rounded-full mr-2" />
                                {catch_}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* CTA */}
                      <div className="mt-6 pt-6 border-t border-slate-200">
                        <a
                          href={`/search?area=${encodeURIComponent(neighborhood.name)}`}
                          className="inline-flex items-center text-[#DE6E4B] font-medium hover:text-[#0A2342] transition-colors"
                          data-testid={`neighborhood-cta-${neighborhood.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          Find Apartments in {neighborhood.name}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </a>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-50 py-16">
        <div className="container-main text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0A2342] mb-4 font-['Playfair_Display']">
            Not Sure Which Area is Right for You?
          </h2>
          <p className="text-slate-600 mb-8 max-w-xl mx-auto">
            Tell us your priorities and we'll recommend the perfect neighborhood based on your lifestyle and budget.
          </p>
          <a href="/search" className="btn-primary" data-testid="neighborhood-cta-search">
            Get Personalized Recommendations
          </a>
        </div>
      </section>
    </div>
  );
};

export default NeighborhoodsPage;
